import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { ISelectOption } from "components/Select/Select"
import LabelCategories, { ICreateLabelCategory } from "models/LabelCategories"
import AssetService from "services/AssetService"
import toastMessages from "stores/toastMessages"
import { Guid } from "@/types"

export default function useLabelCategories({ autoFetch }: { autoFetch?: boolean } = {}) {
    const { t } = useTranslation()
    const [labelCategories, setLabelCategories] = useState<LabelCategories>()

    const fetchLabelCategories = useCallback(async () => {
        try {
            const resp = await AssetService.getLabelCategories()
            setLabelCategories(resp)
        } catch (e) {
            toastMessages.showSystemError()
        }
    }, [])

    useEffect(() => {
        if (!autoFetch) return
        fetchLabelCategories()
    }, [fetchLabelCategories, autoFetch])

    const labelCategoryOptions = useMemo<ISelectOption<string | null>[] | undefined>(() => {
        if (!labelCategories) return
        return [
            ...labelCategories.items.map(lc => ({
                label: lc.name,
                value: lc.id
            })),
            {
                label: t('labelCategories.noCategory'),
                value: null
            }
        ]
    }, [labelCategories, t])

    const createLabelCategory = useCallback(async ({ dto, createLink }: ICreateLabelCategory) => {
        try {
            await AssetService.createLabelCategory(dto, createLink)
            await fetchLabelCategories()
        } catch(e) {
            toastMessages.showSystemError()
        }
    }, [fetchLabelCategories])

    const getLabelCategoryName = useCallback((id: Guid) => {
        if (!labelCategories) return t('common.loading')
        return labelCategories?.items.find(lc => lc.id === id)?.name ?? ""
    }, [labelCategories, t])

    return {
        labelCategories,
        labelCategoryOptions,
        fetchLabelCategories,
        createLabelCategory,
        getLabelCategoryName,
    }

}
